import React, { PropsWithChildren, createContext } from 'react';
import { observer } from 'mobx-react';
import {
    HBStatsigProvider,
    HBStatsigProviderProps
} from '~/common/Statsig/HBStatsigProvider';
import { isReactFirst } from '~/utils/environment.utils';
import { useIsWrappedWithUnauthenticatedStatsigProvider } from '~/common/Statsig/Unauthenticated/useIsWrappedWithUnauthenticatedStatsigProvider';
import { useWarnAboutNestedUnauthenticatedStatsigProvider } from '~/common/Statsig/Unauthenticated/useWarnAboutNestedUnauthenticatedStatsigProvider';
import { useUnauthenticatedGlobal } from '~/providers/unauthenticated-global-context/useUnauthenticatedGlobal';

// Create a context to track nesting (if a StatsigProvider already exists as a parent)
export const HBUnauthenticatedStatsigProviderExistsContext =
    createContext<boolean>(false);

const HBUnauthenticatedStatsigProvider = ({
    children,
    name,
    // in react first every provider is nested, so we silence the warning
    silenceIsNestedWarning = isReactFirst() ? true : false
}: PropsWithChildren<HBStatsigProviderProps>) => {
    const { configStore, networkStore } = useUnauthenticatedGlobal();

    const isWrappedWithUnauthenticatedStatsigProvider =
        useIsWrappedWithUnauthenticatedStatsigProvider();

    useWarnAboutNestedUnauthenticatedStatsigProvider({
        silenceIsNestedWarning,
        name
    });

    // Use the context to determine if this provider is nested (already has a provider as parent)
    if (isWrappedWithUnauthenticatedStatsigProvider) {
        return <>{children}</>;
    }

    return (
        <HBStatsigProvider
            configStore={configStore}
            networkStore={networkStore}
        >
            {/*tell all children that they're wrapped with a provider*/}
            <HBUnauthenticatedStatsigProviderExistsContext.Provider
                value={true}
            >
                {children}
            </HBUnauthenticatedStatsigProviderExistsContext.Provider>
        </HBStatsigProvider>
    );
};

export default observer(HBUnauthenticatedStatsigProvider);
